// Mapeo de step keys a rutas del frontend para Persona Moral
const PERSONA_MORAL_ROUTE_MAP: Record<string, string> = {
  password_change: 'password-change',
  company_data: 'company-data',
  company_bureau_check: 'company-bureau',
  shareholder_data: 'shareholder-data',
  shareholder_bureau_check: 'shareholder-bureau',
  ciec_consultation: 'ciec',
  legal_representative_data: 'legal-representative',
  document_upload: 'documents',
  ocular_visit_scheduling: 'schedule-visit',
  quote_review_acceptance: 'quote-review',
  payment_method_selection: 'payment-method',
  contract_signing_scheduling: 'schedule-signing',
}

// Mapeo de step keys a rutas del frontend para Persona Física
const PERSONA_FISICA_ROUTE_MAP: Record<string, string> = {
  password_change: 'password-change',
  personal_data: 'personal-data',
  bureau_check: 'bureau-check-individual',
  ciec_consultation: 'ciec-individual',
  document_upload: 'documents-individual',
  ocular_visit_scheduling: 'schedule-visit-individual',
  quote_review_acceptance: 'quote-review',
  payment_method_selection: 'payment-method',
  contract_signing_scheduling: 'schedule-signing',
}

// Mapeo unificado (para compatibilidad retroactiva)
const STEP_ROUTE_MAP: Record<string, string> = {
  ...PERSONA_MORAL_ROUTE_MAP,
  // Los específicos de persona física se resolverán dinámicamente
}

// Mapeo inverso: de rutas a step keys (incluye ambos tipos de flujo)
const ROUTE_STEP_MAP: Record<string, string> = {
  ...Object.entries(PERSONA_MORAL_ROUTE_MAP).reduce(
    (acc, [stepKey, route]) => {
      acc[route] = stepKey
      return acc
    },
    {} as Record<string, string>
  ),
  ...Object.entries(PERSONA_FISICA_ROUTE_MAP).reduce(
    (acc, [stepKey, route]) => {
      acc[route] = stepKey
      return acc
    },
    {} as Record<string, string>
  ),
}

/**
 * Obtiene la ruta del frontend para un step key dado
 */
export const getRouteForStep = (
  stepKey: string,
  flowVariant?: 'persona_moral' | 'persona_fisica'
): string => {
  let routeMap = STEP_ROUTE_MAP

  // Usar el mapeo específico según el tipo de flujo si se proporciona
  if (flowVariant === 'persona_fisica') {
    routeMap = PERSONA_FISICA_ROUTE_MAP
  } else if (flowVariant === 'persona_moral') {
    routeMap = PERSONA_MORAL_ROUTE_MAP
  }

  const route = routeMap[stepKey]
  return route ? `/onboarding/${route}` : '/onboarding'
}

/**
 * Obtiene el step key para una ruta dada
 */
export const getStepForRoute = (route: string): string | null => {
  // Extraer la parte después de /onboarding/
  const routePart = route.replace('/onboarding/', '')
  return ROUTE_STEP_MAP[routePart] || null
}

/**
 * Verifica si una ruta es parte del onboarding
 */
export const isOnboardingRoute = (route: string): boolean => {
  return route.startsWith('/onboarding')
}

/**
 * Obtiene todas las rutas de onboarding disponibles
 */
export const getAllOnboardingRoutes = (): string[] => {
  return Object.values(STEP_ROUTE_MAP)
}

/**
 * Obtiene todos los step keys disponibles
 */
export const getAllStepKeys = (): string[] => {
  return Object.keys(STEP_ROUTE_MAP)
}

/**
 * Configuración de pasos por tipo de persona (basado en backend flow-setup.service.ts)
 */
export const PERSONA_MORAL_STEPS = [
  // Etapa 1: Configuración y datos básicos (9 pasos)
  'password_change',
  'company_data',
  'company_bureau_check',
  'shareholder_data',
  'shareholder_bureau_check',
  'ciec_consultation',
  'legal_representative_data',
  'document_upload',
  'ocular_visit_scheduling',

  // Etapa 2: Revisión y firma (3 pasos)
  'quote_review_acceptance',
  'payment_method_selection',
  'contract_signing_scheduling',
] as const

export const PERSONA_FISICA_STEPS = [
  // Etapa 1: Datos básicos (6 pasos)
  'password_change',
  'personal_data',
  'bureau_check',
  'ciec_consultation',
  'document_upload',
  'ocular_visit_scheduling',

  // Etapa 2: Revisión y firma (3 pasos)
  'quote_review_acceptance',
  'payment_method_selection',
  'contract_signing_scheduling',
] as const

/**
 * Obtiene los pasos según el tipo de persona
 */
export const getStepsForPersonType = (
  isCompany: boolean
): readonly string[] => {
  return isCompany ? PERSONA_MORAL_STEPS : PERSONA_FISICA_STEPS
}

/**
 * Verifica si un step key es válido para el tipo de persona
 */
export const isValidStepForPersonType = (
  stepKey: string,
  isCompany: boolean
): boolean => {
  const validSteps = getStepsForPersonType(isCompany)
  return validSteps.includes(stepKey as any)
}

/**
 * Obtiene el nombre amigable de un step (basado en documentación del backend)
 */
export const getStepDisplayName = (stepKey: string): string => {
  const stepNames: Record<string, string> = {
    password_change: 'Cambio de Contraseña',
    company_data: 'Datos de la Empresa',
    personal_data: 'Datos Personales',
    company_bureau_check: 'Consulta Buró Empresarial',
    shareholder_data: 'Datos Accionista Mayoritario',
    shareholder_bureau_check: 'Consulta Buró Accionista',
    bureau_check: 'Consulta Buró de Crédito',
    ciec_consultation: 'Consulta CIEC',
    legal_representative_data: 'Datos Representante Legal',
    document_upload: 'Carga de Documentos',
    ocular_visit_scheduling: 'Programación Visita Ocular',
    quote_review_acceptance: 'Revisión de Cotización',
    payment_method_selection: 'Método de Pago',
    contract_signing_scheduling: 'Programación de Firma',
  }

  return stepNames[stepKey] || stepKey
}

/**
 * Obtiene información sobre las etapas del onboarding
 */
export const getStageInfo = (
  stepKey: string
): { stage: number; stageName: string } => {
  // Etapa 1: Configuración y datos básicos
  const stage1Steps = [
    'password_change',
    'company_data',
    'personal_data',
    'company_bureau_check',
    'shareholder_data',
    'shareholder_bureau_check',
    'bureau_check',
    'ciec_consultation',
    'legal_representative_data',
    'document_upload',
    'ocular_visit_scheduling',
  ]

  // Etapa 2: Revisión y firma
  const stage2Steps = [
    'quote_review_acceptance',
    'payment_method_selection',
    'contract_signing_scheduling',
  ]

  if (stage1Steps.includes(stepKey)) {
    return { stage: 1, stageName: 'Configuración y Verificación' }
  } else if (stage2Steps.includes(stepKey)) {
    return { stage: 2, stageName: 'Cotización y Firma' }
  }

  return { stage: 1, stageName: 'Configuración y Verificación' }
}

/**
 * Valida que una ruta de onboarding sea accesible
 */
export const validateOnboardingRoute = (
  route: string,
  userProgress?: any
): {
  isValid: boolean
  reason?: string
  suggestedRoute?: string
} => {
  // Verificar que sea una ruta de onboarding válida
  if (!isOnboardingRoute(route)) {
    return {
      isValid: false,
      reason: 'No es una ruta de onboarding válida',
      suggestedRoute: '/onboarding',
    }
  }

  // Si es la ruta base de onboarding, siempre es válida
  if (route === '/onboarding') {
    return { isValid: true }
  }

  // Verificar que el step key exista
  const stepKey = getStepForRoute(route)
  if (!stepKey) {
    return {
      isValid: false,
      reason: 'Step key no encontrado para esta ruta',
      suggestedRoute: '/onboarding',
    }
  }

  // Si hay progreso del usuario, verificar que pueda acceder a este paso
  if (userProgress) {
    const currentStep = userProgress.steps?.find(
      (step: any) => step.stepKey === stepKey
    )

    if (!currentStep) {
      return {
        isValid: false,
        reason: 'Este paso no está disponible en tu flujo actual',
        suggestedRoute:
          getRouteForStep(
            userProgress.currentStep,
            userProgress.flow?.variant
          ) || '/onboarding',
      }
    }

    // Verificar que el paso no esté completado (a menos que sea para revisión)
    if (currentStep.status === 'completed') {
      // Permitir acceso a pasos completados para revisión
      return { isValid: true }
    }

    // Verificar que sea el paso actual o que pasos anteriores estén completados
    const stepOrder = currentStep.stepOrder || 0
    const hasUncompletedPreviousSteps = userProgress.steps?.some(
      (step: any) => step.stepOrder < stepOrder && step.status !== 'completed'
    )

    if (hasUncompletedPreviousSteps) {
      return {
        isValid: false,
        reason: 'Hay pasos anteriores sin completar',
        suggestedRoute:
          getRouteForStep(
            userProgress.currentStep,
            userProgress.flow?.variant
          ) || '/onboarding',
      }
    }
  }

  return { isValid: true }
}
